import React from 'react';
import { X } from 'lucide-react';
import type { EmploymentType } from '../hooks/useProvisionDriver';
import type { Company, Vehicle } from '../lib/types';

export type NewDriverForm = {
  full_name: string;
  email: string;
  phone: string;
  driver_code: string;
  license_no: string;
  password: string;
  company_id: string;
  vehicle_id: string;
  employment_type: EmploymentType | '';
  is_insured: boolean;
  insurance_company: string;
  insurance_policy_no: string;
  send_email: boolean;
};

type AddDriverModalProps = {
  open: boolean;
  onClose: () => void;
  newDriver: NewDriverForm;
  setNewDriver: React.Dispatch<React.SetStateAction<NewDriverForm>>;
  showCompanyPicker: boolean;
  companies: Company[];
  availableVehicles: Vehicle[];
  adding: boolean;
  onProvision: () => void;
  onCompanyChange: (companyId: string) => void;
};

const AddDriverModal: React.FC<AddDriverModalProps> = ({
  open,
  onClose,
  newDriver,
  setNewDriver,
  showCompanyPicker,
  companies,
  availableVehicles,
  adding,
  onProvision,
  onCompanyChange,
}) => {
  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-modal w-full max-w-lg animate-slide-up overflow-hidden max-h-[90vh] flex flex-col">
        <div
          className="px-6 py-4 flex items-center justify-between shrink-0"
          style={{ background: 'linear-gradient(135deg, #0B1120, #0f2044)' }}
        >
          <div>
            <h2 className="font-bold text-white" style={{ fontFamily: 'Space Grotesk, Inter, sans-serif' }}>
              Provision New Driver
            </h2>
            <p className="text-xs text-white/60 mt-0.5">
              Official taxilegitimation number → mobile login + DG-plånbok + P-ivilotid
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4 overflow-y-auto">
          {showCompanyPicker && (
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1.5">Company *</label>
              <select
                value={newDriver.company_id}
                onChange={(e) => {
                  const id = e.target.value;
                  setNewDriver((prev) => ({ ...prev, company_id: id, vehicle_id: '' }));
                  onCompanyChange(id);
                }}
                className="input-premium"
              >
                <option value="">Select company…</option>
                {companies.filter((c) => c.status === 'active').map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>
          )}
          {[
            { label: 'Full Name *', key: 'full_name' as const, type: 'text', placeholder: 'e.g. Erik Andersson' },
            { label: 'Förarkod (taxilegitimation) *', key: 'driver_code' as const, type: 'text', placeholder: 'Official number from driver card' },
            { label: 'Email *', key: 'email' as const, type: 'email', placeholder: 'erik@mail.com' },
            { label: 'Phone', key: 'phone' as const, type: 'text', placeholder: '+46 70 000 0000' },
            { label: 'Körkort (optional)', key: 'license_no' as const, type: 'text', placeholder: 'e.g. driving licence number' },
            { label: 'Temp password (optional)', key: 'password' as const, type: 'text', placeholder: 'Auto-generated if empty' },
          ].map((f) => (
            <div key={f.key}>
              <label className="block text-sm font-medium text-slate-600 mb-1.5">{f.label}</label>
              <input
                type={f.type}
                placeholder={f.placeholder}
                value={newDriver[f.key]}
                onChange={(e) => setNewDriver((prev) => ({ ...prev, [f.key]: e.target.value }))}
                className="input-premium"
              />
            </div>
          ))}
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1.5">Vehicle *</label>
            <select
              value={newDriver.vehicle_id}
              onChange={(e) => setNewDriver((prev) => ({ ...prev, vehicle_id: e.target.value }))}
              className="input-premium"
            >
              <option value="">Select vehicle…</option>
              {availableVehicles.map((v) => (
                <option key={v.id} value={v.id}>{v.plate} — {v.model}</option>
              ))}
            </select>
            {availableVehicles.length === 0 && (
              <p className="text-xs text-amber-600 mt-1">No active vehicles — add one under Vehicles first.</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-600 mb-1.5">Employment type</label>
            <select
              value={newDriver.employment_type}
              onChange={(e) => setNewDriver((prev) => ({ ...prev, employment_type: e.target.value as EmploymentType | '' }))}
              className="input-premium"
            >
              <option value="">Not set</option>
              <option value="full_time">Heltid</option>
              <option value="part_time">Deltid</option>
              <option value="arbetsformedlingen">Via Arbetsförmedlingen</option>
            </select>
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={newDriver.is_insured}
              onChange={(e) => setNewDriver((prev) => ({ ...prev, is_insured: e.target.checked }))}
              className="rounded border-slate-300"
            />
            Driver is insured
          </label>
          {newDriver.is_insured && (
            <>
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1.5">Insurance company</label>
                <input
                  type="text"
                  value={newDriver.insurance_company}
                  onChange={(e) => setNewDriver((prev) => ({ ...prev, insurance_company: e.target.value }))}
                  className="input-premium"
                  placeholder="e.g. Folksam"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-600 mb-1.5">Policy number</label>
                <input
                  type="text"
                  value={newDriver.insurance_policy_no}
                  onChange={(e) => setNewDriver((prev) => ({ ...prev, insurance_policy_no: e.target.value }))}
                  className="input-premium"
                  placeholder="e.g. FS-8842-QC"
                />
              </div>
            </>
          )}
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input
              type="checkbox"
              checked={newDriver.send_email}
              onChange={(e) => setNewDriver((prev) => ({ ...prev, send_email: e.target.checked }))}
              className="rounded border-slate-300"
            />
            Send invite email (requires Resend API key on Edge Function)
          </label>
          <div className="flex gap-2 pt-2">
            <button type="button" onClick={onProvision} disabled={adding} className="btn-primary text-sm px-6 py-2.5 disabled:opacity-60">
              {adding ? 'Provisioning…' : 'Provision Driver'}
            </button>
            <button type="button" onClick={onClose} className="btn-ghost text-sm">Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddDriverModal;
